import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader, XCircle, Search, X } from 'lucide-react';
import { showResultPage } from '../../api/BackendApi.jsx'; 
import ActionButton from '../../components/ActionButton.jsx'; 

// This page shows the paginated list of all answers for an analysis
const AnalysisDetailsPage = () => {
    const navigate = useNavigate();
    const { analysisId } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({
        message: '',
        page: 1,
        maxPage: 1,
        solution: [],
        allCategories: [],
    });

    // Search state
    const [originalAnswerQuery, setOriginalAnswerQuery] = useState('');
    const [categoryInput, setCategoryInput] = useState('');
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

    // Filter available category options based on input and already selected categories
    const matchingCategories = useMemo(() => {
        if (!categoryInput.trim() || !data.allCategories) return [];
        return data.allCategories.filter(
            (cat) =>
                cat.toLowerCase().includes(categoryInput.toLowerCase()) &&
                !selectedCategories.includes(cat)
        );
    }, [categoryInput, selectedCategories, data.allCategories]);

    // Fetch paginated data
    const handleFetchPage = useCallback(async (pageNumber) => {
        if (!analysisId) {
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const result = await showResultPage(analysisId, pageNumber, originalAnswerQuery, selectedCategories);
            setData(result);
        } catch (err) {
            setError(err.message || 'Nepodařilo se načíst data.');
        } finally {
            setIsLoading(false);
        }
    }, [analysisId, originalAnswerQuery, selectedCategories]);

    // Fetch the first page on component mount
    useEffect(() => {
        if (analysisId) {
            handleFetchPage(1);
        } else {
            setIsLoading(false);
            setError("ID analýzy nebylo nalezeno (URL ani prop).");
        }
    }, [analysisId, handleFetchPage]);

    // Category selection handlers
    const handleAddCategory = (category) => {
        setSelectedCategories((prev) => [...prev, category]);
        setCategoryInput('');
        setIsCategoryDropdownOpen(false);
    };

    const handleRemoveCategory = (categoryToRemove) => {
        setSelectedCategories((prev) =>
            prev.filter((cat) => cat !== categoryToRemove)
        );
    };

    // Trigger backend request with current search filters
    const handleSearchSubmit = (e) => {
        if (e) e.preventDefault();
        handleFetchPage(1);
    };

    const handleResetSearch = () => {
        setOriginalAnswerQuery('');
        setSelectedCategories([]);
        setCategoryInput('');
        handleFetchPage(1, { query: '', categories: [] });
    };
    
    const { page, maxPage, solution, message } = data;
    const itemsPerPage = 50;

    // Render the main content
    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="flex flex-col items-center justify-center h-96 text-center">
                    <Loader className="h-12 w-12 mb-4 text-blue-600 animate-spin" />
                    <h3 className="text-xl font-bold text-gray-800">Načítání výsledků...</h3>
                </div>
            );
        }

        if (error) {
            return (
                <div className="flex flex-col items-center justify-center h-96 text-center text-red-600">
                    <XCircle className="h-12 w-12 mb-4" />
                    <h3 className="text-xl font-bold">Chyba při načítání</h3>
                    <p className="mt-2 text-gray-600 max-w-md">{error}</p>
                </div>
            );
        }

        return (
            <div className="w-full h-full flex flex-col min-h-0">
                <p className="text-gray-600 mb-4 text-center font-semibold">{message}</p>
                
                <div className="flex-grow bg-gray-50 p-4 rounded-lg border overflow-y-auto" style={{ minHeight: '400px' }}>
                    {/* Header */}
                    <div className="text-gray-700 font-semibold flex items-center text-sm bg-gray-200 p-2 rounded-md mb-2 sticky top-0">
                        <span className="text-gray-500 font-mono w-12 text-right mr-4">#</span>
                        <span className="flex-1">ID respondenta</span>
                        <span className="flex-1">Původní odpověď</span>
                        <span className="flex-1 px-3">Přiřazená kategorie</span>
                    </div>

                    {/* Data rows */}
                    <ul className="space-y-2">
                    {solution && solution.length > 0 ? solution.map((item, index) => (
                        <li
                        key={index}
                        className="text-gray-800 flex items-center text-sm bg-white p-2 rounded-md shadow-sm"
                        >
                            <span className="text-gray-400 font-mono w-12 text-right mr-4">
                                {index + 1 + (page - 1) * itemsPerPage}.
                            </span>
                            <span className="flex-1 font-medium">{item[0]}</span>
                            <span className="flex-1 font-medium">{item[1]}</span>
                            <span className="flex-1 font-semibold text-purple-600 px-3 py-1 text-xs">
                                {item[2].join(", ")}
                            </span>
                        </li>
                    )) : (
                        <li className="text-gray-500 text-center p-4">Žádná data k zobrazení.</li>
                    )}
                    </ul>
                </div>
            </div>
        );
    };

    return (
        <div className="flex justify-center min-h-full p-4 pt-8 pb-16">
            <div className="w-full max-w-4xl">
                <div className="flex flex-col items-center w-full">

                    {/* Header */}
                    <div className="w-full text-center mb-6">
                        <h2 className="text-3xl font-bold text-gray-800">Detailní výsledky</h2>
                        <p className="text-lg text-gray-600 mt-2">Jednotlivé odpovědi a jejich klasifikace</p>
                    </div>

                    {/* Toolbar */}
                    <div className="w-full flex flex-col md:flex-row gap-4 mb-6 pb-6 border-b border-gray-200">
                        <ActionButton 
                            onClick={() => navigate(`/analysis/${analysisId}/summary`)}
                            className="w-full md:w-auto"
                            variant="outline"
                            icon={<ChevronLeft size={18} />}
                        >
                            Zpět na souhrn
                        </ActionButton>
                    </div>

                    {/* Search Controls Form */}
                    <form onSubmit={handleSearchSubmit} className="w-full bg-white p-4 rounded-lg border border-gray-200 shadow-sm mb-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            
                            {/* 1. Search by 'Původní odpověď' */}
                            <div className="flex flex-col">
                                <label className="text-sm font-semibold text-gray-700 mb-1">
                                    Původní odpověď
                                </label>
                                <input
                                    type="text"
                                    value={originalAnswerQuery}
                                    onChange={(e) => setOriginalAnswerQuery(e.target.value)}
                                    placeholder="Hledat v textu odpovědi..."
                                    className="w-full px-3 py-2 border rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* 2. Search by 'Přiřazená kategorie' */}
                            <div className="flex flex-col relative">
                                <label className="text-sm font-semibold text-gray-700 mb-1">
                                    Přiřazená kategorie
                                </label>
                                
                                {/* Selected badges */}
                                {selectedCategories.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-2">
                                        {selectedCategories.map((category) => (
                                            <span
                                                key={category}
                                                className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-1 rounded-full"
                                            >
                                                {category}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveCategory(category)}
                                                    className="hover:text-purple-900"
                                                >
                                                    <X size={12} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Autocomplete input */}
                                <input
                                    type="text"
                                    value={categoryInput}
                                    onChange={(e) => {
                                        setCategoryInput(e.target.value);
                                        setIsCategoryDropdownOpen(true);
                                    }}
                                    onFocus={() => setIsCategoryDropdownOpen(true)}
                                    placeholder="Vyhledat a přidat kategorii..."
                                    className="w-full px-3 py-2 border rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500"
                                />

                                {/* Matching Options Dropdown */}
                                {isCategoryDropdownOpen && matchingCategories.length > 0 && (
                                    <ul className="absolute z-10 top-full left-0 right-0 bg-white border rounded-md shadow-lg max-h-40 overflow-y-auto mt-1">
                                        {matchingCategories.map((cat) => (
                                            <li
                                                key={cat}
                                                onClick={() => handleAddCategory(cat)}
                                                className="px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-700 cursor-pointer transition-colors"
                                            >
                                                {cat}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>

                        {/* Search Action Buttons */}
                        <div className="flex justify-end gap-2 pt-2 border-t">
                            <button
                                type="button"
                                onClick={handleResetSearch}
                                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Resetovat
                            </button>
                            <ActionButton
                                type="submit"
                                icon={<Search size={16} />}
                                className="px-4 py-2 text-sm"
                            >
                                Vyhledat
                            </ActionButton>
                        </div>
                    </form>

                    {/* Main Content */}
                    <div className="w-full">
                        {renderContent()}
                    </div>

                    {/* Pagination and Navigation */}
                    {!isLoading && !error && (
                        <div className="flex flex-col items-center gap-4 w-full pt-6 mt-4 border-t border-gray-200">
                            <div className="flex items-center justify-center gap-4">
                                <button onClick={() => handleFetchPage(page - 1)} disabled={page <= 1 || isLoading} className="p-2 rounded-full bg-white border disabled:opacity-50 hover:bg-gray-100 transition-colors">
                                    <ChevronLeft size={20} />
                                </button>
                                <span className="font-semibold text-gray-700">
                                    Strana {page} / {maxPage}
                                </span>
                                <button onClick={() => handleFetchPage(page + 1)} disabled={page >= maxPage || isLoading} className="p-2 rounded-full bg-white border disabled:opacity-50 hover:bg-gray-100 transition-colors">
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AnalysisDetailsPage;

